import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import styles from "./GoLivePayg.module.css";
import axios from "axios";
import { API_BASE_URL, getAgentById as fetchAgentById } from "../../Store/apiStore";
import { useAgentStore } from "../../Store/agentDetailStore";
import decodeToken from "../../lib/decodeToken";
import { setPaymentCancelReturn } from "../PaymentCancel/PaymentCancel";
import { trackEvent, stashPendingPurchase } from "../../lib/analytics";
import CostCalculatorModal from "./CostCalculatorModal";
import ChoosePlanModal from "../TrialOffer/ChoosePlanModal";
import { priceOf, findPlan } from "../TrialOffer/plans";
import { markGoLiveCheckoutRedirect } from "./GoLiveVariant";
import AnimatedButton2 from "../AnimatedButton/AnimatedButton2";
import { useGlobalCall, isMobileAppSurface } from "../../contexts/GlobalCallContext";
import { useOfferStore, getSessionCallCount } from "../../Store/offerStore";
import { isAgentRecentlyPaid } from "../../lib/paymentGuard";
import { isVariantB } from "../../lib/experiments";
import { isEmbeddedCheckoutEnabled } from "../../lib/embeddedCheckoutFlag";
import { useCheckoutStore } from "../../Store/checkoutStore";
import { PAYG_PRICING, FREE_MINUTES, detectPaygPricing, feeText, rateText } from "../../lib/paygPricing";

// "Go live for $5/mo" screen shown inside the Android WebView shell.
// Reuses the same /quick-setup-payg checkout gateway GetNumber uses, but on
// success Stripe returns to /go-live-setup?agentId=<agentId> (the post agent
// creation go-live flow) instead of /number-assign.
const goLiveFeatures = [
    "Your own local business number",
    "24/7 call answering in a natural voice",
    "Automatic calendar appointment booking",
    "Call triage & routing — your rules",
    "Spam call blocking",
];

const GoLivePayg = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showCalc, setShowCalc] = useState(false);
    // Fee / per-minute rate for the visitor's country: Canada → CAD, else USD.
    // Starts on USD so nothing renders blank while the country is looked up.
    const [pricing, setPricing] = useState(PAYG_PRICING.usd);
    useEffect(() => {
        let alive = true;
        detectPaygPricing().then((p) => alive && setPricing(p));
        return () => {
            alive = false;
        };
    }, []);
    // The agent speaks plain "$8"; for Canada it is told these are Canadian dollars.
    const cadNote = pricing.currency === "cad" ? " All prices are in Canadian dollars (CAD)." : "";
    // 'Plans $119–$599/mo' → opens the full plan-selection sheet.
    const [planOpen, setPlanOpen] = useState(false);
    // Stripe embedded checkout sheet khuli ho to Go Live bar chhupa do — ye
    // body par portal hai, iPhone pe sheet ke upar aa jata tha.
    const isCheckoutOpen = useCheckoutStore((s) => s.open);
    const [planYearly, setPlanYearly] = useState(false);
    const [planSelected, setPlanSelected] = useState("scaler");
    // User changed the plan in the sheet → reveal the Continue button (same
    // gating as the trial: hidden until agreed OR plan changed).
    const [planChanged, setPlanChanged] = useState(false);
    // This is the PAYG ($5) screen → agreement is tracked under the "payg" tier.
    const agreed = useOfferStore((s) => !!s.agreedTiers?.payg);
    // Agent already tested before (call history) → don't gate the pay button
    // behind agreement; the confirm_go_live gating is only for a brand-new
    // agent's FIRST demo call.
    const hasPriorCalls =
        (useOfferStore((s) => s.priorCallCount) || 0) > 0 ||
        getSessionCallCount(sessionStorage.getItem("SelectAgentId")) > 0;
    // True once a real call has happened AND ended this session.
    const callEnded = useOfferStore((s) => s.callEnded);
    // Stripe products (for real plan checkout when a plan is picked in the sheet).
    const [planProducts, setPlanProducts] = useState([]);

    // Agent details from the global store. `agentData` is shaped { agent, business }.
    const { agentData, setAgentById, getAgentById } = useAgentStore();
    const agentId = sessionStorage.getItem("SelectAgentId") || "";
    const cameFromUpgrade = Boolean(
        (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("from") === "upgrade") ||
        (typeof sessionStorage !== "undefined" && sessionStorage.getItem("updateBtn") === "update") ||
        (typeof sessionStorage !== "undefined" && Boolean(sessionStorage.getItem("upgradeReturnTo")))
    );

    // Live global call (Android/iOS) — used to have the agent acknowledge the
    // payment, then cut the call, right before we leave for Stripe.
    const { isCallLive, injectContext, endGlobalCall, awaitAgentIdle } = useGlobalCall();

    // Pay button is hidden ONLY while a live sales call is in progress and the
    // owner hasn't agreed yet (confirm_go_live tool). Once the call ends — or
    // there's no call at all (normal /recommended-plan visit) — it always shows.
    // The plan sheet being open also shows it so the plan-checkout is reachable.
    // $5 pay button ab HAMESHA dikhta hai. Pehle ye
    //   !isMobileAppSurface() || agreed || planChanged || callEnded || hasPriorCalls
    // tha — mobile par button chhupa rehta jab tak agent confirm_go_live na kare,
    // ya user plan na badle, ya call khatam na ho, ya ye 2nd+ call na ho. Gating
    // hata di gayi: user ko kharidne ka rasta turant milna chahiye.
    //
    // `agreed` abhi bhi chahiye — neeche wala effect uske badalne par agent ko
    // "button dikh gaya, tap karne ko kaho" wala nudge bhejta hai.
    const showPayCTA = true;

    // ONLY when the button appears via the confirm_go_live tool (agreed) — not
    // the other reveal paths — nudge the live agent (update-live) to invite the
    // user to tap the button. injectContext no-ops if there's no live call.
    const payClickNudgedRef = useRef(false);
    useEffect(() => {
        if (agreed && !payClickNudgedRef.current) {
            payClickNudgedRef.current = true;
            injectContext(
                `CONTEXT: The 'Go Laiv for $${pricing.fee}/mo' button is now visible on the user's screen.${cadNote} In ONE short, warm sentence, invite them to tap it to go Laiv. Do NOT read this note aloud.`
            );
        } else if (!agreed) {
            payClickNudgedRef.current = false;
        }
    }, [agreed, injectContext, pricing, cadNote]);

    // Make sure the current agent is loaded into the store so we can show its
    // real name. If it isn't cached yet, fetch it once and cache it.
    useEffect(() => {
        if (!agentId) return;
        if (agentData?.agent?.agentName) return;            // already have it
        if (getAgentById(agentId)?.agentData?.agent?.agentName) return; // in cache
        (async () => {
            try {
                const res = await fetchAgentById(agentId);
                const agent = res?.data || res || {};
                const business = agent?.business || null;
                setAgentById(agentId, { agent, business });
            } catch (err) {
                console.error("[GoLivePayg] agent load failed:", err?.message || err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    // Fetch Stripe products once, so picking a plan in the sheet can check out
    // that plan (not the $5 PAYG). Same /products source RecommendedPlan uses.
    useEffect(() => {
        const token = localStorage.getItem("token") || "";
        fetch(`${API_BASE_URL}/products`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })
            .then((r) => r.json())
            .then((data) => {
                if (Array.isArray(data)) setPlanProducts(data);
            })
            .catch((err) =>
                console.error("[GoLivePayg] products load failed:", err?.message || err)
            );
    }, []);

    // Agent's name — store first, then its multi-agent cache, then "Sophia".
    const agentName =
        agentData?.agent?.agentName ||
        getAgentById(agentId)?.agentData?.agent?.agentName ||
        "Sophia";

    // Open the cost calculator AND, if the agent is live on the call, tell it
    // the customer just opened the calculator so it can react/explain briefly.
    const handleOpenCalc = () => {
        setShowCalc(true);
        if (isCallLive) {
            // Same wording for every country; only the numbers follow the pricing.
            const f = pricing.fee;
            const r = pricing.rate.toFixed(2);
            const free = FREE_MINUTES;
            const cost = (n) => Math.round(f + Math.max(0, n - free) * pricing.rate);
            injectContext(
                "The customer just opened the 'What will <you> cost?' calculator popup. This is the SAME screen, NOT a new screen — do NOT reintroduce yourself, just explain briefly.\n" +
                `PRICING: $${f} per month includes ${free} free minutes. Every minute beyond that costs $${r}. Spam calls are blocked free and never billed.${cadNote}\n` +
                "COST FORMULA for N expected minutes per month:\n" +
                `   cost = $${f} + (N − ${free}) × $${r}   (if N is ${free} or less, the cost is just $${f}).\n` +
                `Example: N = 150 → $${f} + (150 − ${free}) × $${r} = $${cost(150)} per month.\n` +
                `IMPORTANT: $${f} covers ONLY ${free} minutes. NEVER say heavy usage (e.g. 500 minutes) is still $${f} — that is wrong; 500 minutes = $${f} + (${500 - free} × $${r}) = $${cost(500)}/month. Always COMPUTE.\n` +
                "The slider (0–1500 min) updates the figure live, and you alert them before they run out — no surprises.\n" +
                `TASK: When they ask 'how much for X minutes?', COMPUTE it with this formula and answer in ONE short line (max 40 words), e.g. '150 minutes is about $${cost(150)} a month.' Otherwise just say: $${f}/month with ${free} free minutes, then $${r} per extra minute — drag the slider to estimate.`
            );
        }
    };

    // Checkout the plan currently SELECTED in the sheet (Starter/Scaler/Growth)
    // via the same Stripe subscription flow RecommendedPlan uses. Used when the
    // plan sheet is open (button shows the plan price instead of $5 PAYG).
    const handlePlanCheckout = async (planKeyOverride) => {
        if (isAgentRecentlyPaid(sessionStorage.getItem("SelectAgentId"))) {
            navigate("/fast-agent-detail", { replace: true });
            return;
        }
        try {
            setLoading(true);
            // On a live call: have the agent thank them + finish its line before
            // we navigate to checkout (the redirect tears the page down).
            if (isCallLive) {
                injectContext(
                    "The customer just tapped the pay button on the SAME screen — do NOT reintroduce yourself. In ONE short, warm sentence, thank them and tell them to complete the payment and come right back. Do not ask any questions."
                );
                await awaitAgentIdle();
            }
            const token = localStorage.getItem("token") || "";
            const decoded = decodeToken(token);
            if (!decoded || !token) {
                console.error("User not authenticated");
                setLoading(false);
                return;
            }
            const customerId = decoded.customerId;
            const userId = decoded.id;
            const agentId = sessionStorage.getItem("SelectAgentId") || "";

            // Optional explicit plan (e.g. the calculator's "Subscribe and Save"
            // recommends a plan). When called from a button onClick the arg is an
            // event, so only honour a real string key; otherwise use the sheet's
            // current selection.
            const planKey =
                typeof planKeyOverride === "string" ? planKeyOverride : planSelected;
            // Match the selected plan to its Stripe product + price.
            const wantName = (findPlan(planKey)?.name || "").trim().toLowerCase();
            const product = planProducts.find(
                (p) => (p.name || "").trim().toLowerCase() === wantName
            );
            const interval = planYearly ? "year" : "month";
            const priceObj = product?.prices?.find((p) => p.interval === interval);
            if (!priceObj?.id) {
                console.error("[GoLivePayg] no priceId for plan", wantName, interval);
                setLoading(false);
                return;
            }

            // Cancel/back par user ko BILKUL wahi URL milna chahiye jahan se wo
            // checkout pe gaya tha — search string jaisi hai waisi.
            //
            // Pehle yahan `|| "?from=upgrade"` tha. Jab user bare
            // /recommended-plan (yaani $5 / trial wali GoLiveVariant screen) se
            // checkout pe jaata, to return path me from=upgrade chipak jaata —
            // aur RecommendedPlan us param pe view="plans" force kar deta hai.
            // Natija: user $5 screen chhodta tha aur wapas "Choose Your Plan"
            // grid pe girta tha. Ab wahi screen milti hai jo chhodi thi.
            const currentCancelPath =
                window.location.pathname + window.location.search;
            sessionStorage.setItem("cancelReturnTo", currentCancelPath);
            setPaymentCancelReturn(currentCancelPath);

            const successParams = new URLSearchParams({ fromCheckout: "1" });
            if (agentId) successParams.append("agentId", agentId);
            if (userId) successParams.append("userId", userId);
            // Ye plans sheet ($119–$599) ka checkout hai, $5 PAYG ka nahi — to
            // thank-you interstitial nahi. Pehle WebView par ye
            // /payment-success se guzarta tha, isliye mobile par Scaler par bhi
            // thank-you page aa jata tha. (Interstitial sirf neeche wale
            // quick-setup-payg flow ke liye hai.)
            const successUrl = `${window.location.origin}/go-live-setup?${successParams.toString()}`;
            // Match RecommendedPlan's web flow exactly (same cancel route).
            const cancelUrl = `${window.location.origin}/cancel-payment`;

            // Embedded opens Stripe inside this page (CheckoutSheet) so the live
            // call survives; hosted keeps the pre-existing full-page redirect.
            const embedded = isEmbeddedCheckoutEnabled();

            const res = await axios.post(
                `${API_BASE_URL}${embedded ? "/create-checkout-session-embedded" : "/create-checkout-session"}`,
                {
                    customerId,
                    priceId: priceObj.id,
                    userId,
                    // Embedded never redirects, but the backend still needs
                    // these to mint the HOSTED fallback session it returns
                    // alongside the client secret.
                    url: successUrl,
                    cancelUrl,
                    currency: (priceObj.currency || "usd").toLowerCase(),
                    agentId,
                    clientFiresPurchase: true,
                    isUpgrade: true,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const purchaseValue =
                priceObj.unit_amount != null ? priceObj.unit_amount / 100 : undefined;
            const purchaseCurrency = (priceObj.currency || "usd").toUpperCase();
            const purchaseBillingCycle = interval === "year" ? "yearly" : "monthly";

            if (embedded && res?.data?.clientSecret) {
                trackEvent("begin_checkout", {
                    plan: product.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                stashPendingPurchase({
                    transaction_id: `${userId || "uid"}-${Date.now()}`,
                    plan: product.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                /*
                 * Deliberately NOT calling endGlobalCall() or
                 * markGoLiveCheckoutRedirect() here: nothing remounts on this
                 * path, so the call keeps running (the whole point) and that
                 * redirect flag would sit unconsumed and eat the NEXT genuine
                 * visit's offer.
                 */
                useCheckoutStore.getState().openCheckout({
                    clientSecret: res.data.clientSecret,
                    sessionId: res.data.sessionId,
                    hostedUrl: res.data.checkoutUrl || null,
                    meta: {
                        agentId,
                        userId,
                        planName: product.name,
                        value: purchaseValue,
                        currency: purchaseCurrency,
                        next: "/go-live-setup",
                    },
                });
                setLoading(false);
                return;
            }

            if (res.data.checkoutUrl) {
                trackEvent("begin_checkout", {
                    plan: product.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                // Same as RecommendedPlan: stash the deal so the thank-you landing
                // fires the client-side GA4 `purchase` (backend skips it because
                // clientFiresPurchase:true). Without this, no purchase is tracked.
                stashPendingPurchase({
                    transaction_id: `${userId || "uid"}-${Date.now()}`,
                    plan: product.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                // If the agent is live, cut the call before leaving for Stripe.
                if (isCallLive) {
                    try { endGlobalCall(); } catch (_e) { /* ignore */ }
                }
                if (isVariantB()) {
                    try { sessionStorage.removeItem("globalCallBannerEnded"); } catch (_e) { }
                }
                setLoading(false);
                // Keep this same offer if the user returns from checkout.
                markGoLiveCheckoutRedirect();
                window.history.pushState(null, "", "/");
                window.location.replace(res.data.checkoutUrl);
                return;
            }
            setLoading(false);
        } catch (error) {
            console.error("Plan checkout error:", error.response?.data || error.message);
            setLoading(false);
        }
    };

    const handleGoLiveCheckout = async () => {
        // Guard: agent was JUST paid (user back-navigated here) → don't start a
        // second Stripe session. Send them on instead of double-charging.
        if (isAgentRecentlyPaid(sessionStorage.getItem("SelectAgentId"))) {
            navigate("/fast-agent-detail", { replace: true });
            return;
        }
        try {
            setLoading(true);

            // If the agent is live on the call (Android/iOS), have it
            // acknowledge the payment BEFORE we leave for Stripe. The Stripe
            // redirect tears the whole page down (killing the call), so we
            // inject the line, give the agent a moment to say it, then cut the
            // call cleanly and redirect. Injection + checkout-session creation
            // run in parallel so we don't stack the delays.
            const hadLiveCall = isCallLive;
            if (hadLiveCall) {
                // Fire-and-forget the injection — we gate the redirect on a
                // fixed speaking window below, not on this promise.
                injectContext(
                    "The customer just tapped the pay button on the SAME screen — this is NOT a new screen, so do NOT reintroduce yourself. In ONE short, warm sentence, thank them and tell them to complete the payment and come right back. Do not ask any questions."
                );
            }

            const token = localStorage.getItem("token") || "";
            const decoded = decodeToken(token);
            if (!decoded || !token) {
                console.error("User not authenticated");
                setLoading(false);
                return;
            }

            const customerId = decoded.customerId;
            const userId = decoded.id;
            // agentId is already available in this flow (set when the agent was created).
            const agentId = sessionStorage.getItem("SelectAgentId") || "";

            const successParams = new URLSearchParams({ ispayg: "true" });
            if (agentId) successParams.append("agentId", agentId);
            if (userId) successParams.append("userId", userId);
            // "Thank You" / Setup Progress screen ab WEB pe bhi — dono surfaces
            // /payment-success se guzarte hain, jo `next=/go-live-setup` par
            // saare params (ispayg sahit) forward karta hai taaki GoLiveWizard ki
            // PAYG-Extra activation waise hi chale.
            successParams.append("next", "/go-live-setup");
            const successUrl = `${window.location.origin}/payment-success?${successParams.toString()}`;
            const cancelUrl = `${window.location.origin}/payment-cancel`;
            // Cancel par userjj ko WAHIN wapas laana hai jahan se wo checkout pe
            // gaya tha. Stripe ek poora page load hai, to history/in-memory
            // state kuch nahi bachta — path abhi stash karna padta hai.
            // Search string ko waisa hi rakho — `|| "?from=upgrade"` lagane se
            // return par view "Choose Your Plan" grid pe flip ho jaata tha,
            // jabki user $5 wali screen se aaya tha. Upar wale checkout me bhi
            // yahi fix hai.
            const currentCancelPath =
                window.location.pathname + window.location.search;
            sessionStorage.setItem("cancelReturnTo", currentCancelPath);
            setPaymentCancelReturn(currentCancelPath);

            const embeddedPayg = isEmbeddedCheckoutEnabled();

            const response = await axios.post(
                `${API_BASE_URL}${embeddedPayg ? "/quick-setup-payg-embedded" : "/quick-setup-payg"}`,
                {
                    customerId,
                    userId,
                    // Pass agentId so the checkout session carries it → the Stripe
                    // webhook activates PAYG for this agent server-side on payment,
                    // even if the app is closed before the number-assign screen.
                    agentId,
                    url: successUrl,
                    cancelUrl,
                    isAssignNumber: true,
                    // Canada → "cad" (CA$8 + CAD per-minute); everything else "usd".
                    currency: pricing.currency,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (embeddedPayg && response?.data?.clientSecret) {
                trackEvent("begin_checkout", {
                    plan: "Go Live PAYG",
                    billing_cycle: "monthly",
                    value: pricing.fee,
                    currency: pricing.code,
                });
                /*
                 * No awaitAgentIdle() / endGlobalCall() here: the sheet opens
                 * over this page and the agent keeps talking, so there is
                 * nothing to wind down. markGoLiveCheckoutRedirect() is also
                 * skipped — nothing remounts, so the flag would sit unconsumed
                 * and eat the next genuine visit's offer.
                 */
                useCheckoutStore.getState().openCheckout({
                    clientSecret: response.data.clientSecret,
                    sessionId: response.data.sessionId,
                    hostedUrl: response.data.checkoutUrl || null,
                    meta: {
                        agentId,
                        userId,
                        planName: "Go Live PAYG",
                        value: pricing.fee,
                        currency: pricing.code,
                        // Same route as the hosted successUrl above: the Thank
                        // You / Setup Progress interstitial first, then its
                        // Continue forwards every param (incl. ispayg) on to
                        // `next`. Pointing this straight at /go-live-setup is
                        // what skipped the thank-you screen whenever embedded
                        // checkout was on. SPA navigation, so the live call
                        // still survives.
                        next: "/payment-success",
                        // ispayg flag GoLiveWizard branches on. Must match the
                        // hosted successParams value ("true", not "1") exactly.
                        extraParams: { ispayg: "true", next: "/go-live-setup" },
                    },
                });
                setLoading(false);
                return;
            }

            if (response.data.checkoutUrl) {
                // GA4: Stripe checkout opening — last client event in the money flow.
                // Fixed Go Live PAYG plan ($5/mo), so the values are constants here.
                trackEvent("begin_checkout", {
                    plan: "Go Live PAYG",
                    billing_cycle: "monthly",
                    value: pricing.fee,
                    currency: pricing.code,
                });
                // Let the agent actually FINISH the acknowledgement (not a fixed
                // guess), then cut the call before we tear the page down for
                // Stripe. awaitAgentIdle waits for the injected line to start +
                // finish, capped so it can never hang.
                if (hadLiveCall) {
                    await awaitAgentIdle();
                    try { endGlobalCall(); } catch (_e) { /* ignore */ }
                }
                if (isVariantB()) {
                    try { sessionStorage.removeItem("globalCallBannerEnded"); } catch (_e) { }
                }
                // Keep this same offer if the user returns from checkout.
                markGoLiveCheckoutRedirect();
                window.history.pushState(null, "", "/");
                window.location.replace(response.data.checkoutUrl);
                return;
            }
            setLoading(false);
        } catch (error) {
            console.error("Checkout Error:", error.response?.data || error.message);
            setLoading(false);
        }
    };

    return (
        <>
            {loading && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999999,
                        cursor: 'wait',
                        background: 'transparent',
                        pointerEvents: 'all',
                    }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                />,
                document.body
            )}

            <div className={styles.containerBox}>
                <div className={styles.container}>
                    <div className={styles.goLiveWrap}>
                        <h2 className={styles.glTitle}>
                            Go live for <span className={styles.glTitleAccent}>{feeText(pricing)}/mo</span>
                        </h2>
                        <p className={styles.glSub}>
                            Your own business number + everything {agentName} does
                        </p>

                        <h3 className={styles.includedTitle}>
                            Everything included in your {feeText(pricing)}/mo:
                            <span className={styles.includedLine} />
                        </h3>

                        <div className={styles.glFeatures}>
                            {goLiveFeatures.map((text, index) => (
                                <div className={styles.glFeatureRow} key={index}>
                                    <span className={styles.glCheck}>
                                        <img src="/svg/check-circle-fill2.svg" alt="" />
                                    </span>
                                    <p>{text}</p>
                                </div>
                            ))}
                        </div>

                        {cameFromUpgrade && (
                            <button type="button" className={styles.plansLink} onClick={() => setPlanOpen(true)}>
                                Prefer to see plans first? <span aria-hidden="true">›</span>
                            </button>
                        )}

                        {/* Offer card */}
                        <div className={styles.glOfferCard}>
                            <div className={styles.offerFreeRow}>
                                <span className={styles.offerGift} aria-hidden="true">
                                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19.0145 4.88594H16.5291C16.9134 4.3752 17.1415 3.74064 17.1415 3.05371C17.1415 1.3699 15.7716 0 14.0878 0C13.0735 0 12.3256 0.363392 11.734 1.1436C11.2391 1.79636 10.8772 2.70775 10.4233 3.85928C9.96944 2.70771 9.60755 1.79636 9.11265 1.1436C8.52108 0.363392 7.77316 0 6.75888 0C5.07507 0 3.70517 1.3699 3.70517 3.05371C3.70517 3.74064 3.9333 4.3752 4.31758 4.88594H1.83223C0.821937 4.88594 0 5.70788 0 6.71817L0 7.93965C0 8.73586 0.510622 9.41472 1.22149 9.66683L1.22149 19.0145C1.22149 20.0247 2.04342 20.8467 3.05371 20.8467H17.793C18.8033 20.8467 19.6252 20.0247 19.6252 19.0145V9.66683C20.3361 9.41472 20.8467 8.73586 20.8467 7.93965V6.71817C20.8467 5.70788 20.0247 4.88594 19.0145 4.88594ZM11.5444 4.34604C12.48 1.97237 12.8357 1.22149 14.0878 1.22149C15.0981 1.22149 15.92 2.04342 15.92 3.05371C15.92 4.064 15.0981 4.88594 14.0878 4.88594H11.3308C11.4056 4.69804 11.477 4.51713 11.5444 4.34604ZM6.75888 1.22149C8.01095 1.22149 8.36664 1.97237 9.30226 4.34604C9.36969 4.51713 9.44106 4.69804 9.5159 4.88594H6.75888C5.74859 4.88594 4.92666 4.064 4.92666 3.05371C4.92666 2.04342 5.74859 1.22149 6.75888 1.22149ZM7.98037 19.6252H3.05371C2.71695 19.6252 2.44297 19.3512 2.44297 19.0145V9.77188H7.98037L7.98037 19.6252ZM7.98037 8.5504H1.83223C1.49546 8.5504 1.22149 8.27642 1.22149 7.93965V6.71817C1.22149 6.3814 1.49546 6.10743 1.83223 6.10743H7.98037V8.5504ZM11.6448 19.6252H9.20185V6.10743H11.6448V19.6252ZM18.4037 19.0145C18.4037 19.3512 18.1297 19.6252 17.793 19.6252H12.8663V9.77188H18.4037V19.0145ZM19.6252 7.93965C19.6252 8.27642 19.3512 8.5504 19.0145 8.5504H12.8663V6.10743L19.0145 6.10743C19.3512 6.10743 19.6252 6.3814 19.6252 6.71817V7.93965Z" fill="#339E5A" />
                                    </svg>

                                </span>
                                <p>
                                    <strong>{FREE_MINUTES} call minutes</strong> are <strong>FREE</strong> this month
                                </p>
                            </div>

                            <p className={styles.offerRate}>
                                Additional usage charged at{" "}
                                <span className={styles.offerRateInner}>
                                    {rateText(pricing)}<span className={styles.offerRateUnit}>/minute</span>
                                </span>
                            </p>
                            <p className={styles.offerSpam}>
                                Only for <span className={styles.realCalls}>real business calls</span> answered by your AI receptionist. Spam is blocked
                            </p>
                        </div>

                        {/* <button type="button" className={styles.calcCostBtn} onClick={handleOpenCalc}>
                            <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.20254 11.5785H5.35254C5.78853 11.5785 6.12539 11.2215 6.12539 10.8057V9.65567C6.12539 9.21968 5.76839 8.88281 5.35254 8.88281H4.20254C3.76655 8.88281 3.42969 9.23982 3.42969 9.65567V10.8057C3.42969 11.2223 3.76655 11.5785 4.20254 11.5785Z" fill="#444444" />
                                <path d="M8.48574 11.5785H9.63574C10.0717 11.5785 10.4086 11.2215 10.4086 10.8057V9.65567C10.4086 9.21968 10.0516 8.88281 9.63574 8.88281H8.48574C8.04975 8.88281 7.71289 9.23982 7.71289 9.65567V10.8057C7.71289 11.2223 8.04975 11.5785 8.48574 11.5785Z" fill="#444444" />
                                <path d="M8.48574 15.7035H9.63574C10.0717 15.7035 10.4086 15.3465 10.4086 14.9307V13.7807C10.4086 13.3447 10.0516 13.0078 9.63574 13.0078H8.48574C8.04975 13.0078 7.71289 13.3648 7.71289 13.7807V14.9307C7.71289 15.3465 8.04975 15.7035 8.48574 15.7035Z" fill="#444444" />
                                <path d="M4.20254 15.7035H5.35254C5.78853 15.7035 6.12539 15.3465 6.12539 14.9307V13.7807C6.12539 13.3447 5.76839 13.0078 5.35254 13.0078H4.20254C3.76655 13.0078 3.42969 13.3648 3.42969 13.7807V14.9307C3.42969 15.3465 3.76655 15.7035 4.20254 15.7035Z" fill="#444444" />
                                <path d="M12.768 11.5785H13.918C14.354 11.5785 14.6908 11.2215 14.6908 10.8057V9.65567C14.6908 9.21968 14.3338 8.88281 13.918 8.88281H12.768C12.332 8.88281 11.9951 9.23982 11.9951 9.65567V10.8057C11.9943 11.2223 12.3312 11.5785 12.768 11.5785Z" fill="#444444" />
                                <path d="M12.768 15.7035H13.918C14.354 15.7035 14.6908 15.3465 14.6908 14.9307V13.7807C14.6908 13.3447 14.3338 13.0078 13.918 13.0078H12.768C12.332 13.0078 11.9951 13.3648 11.9951 13.7807V14.9307C11.9943 15.3465 12.3312 15.7035 12.768 15.7035Z" fill="#444444" />
                                <path d="M4.77835 3.64738C5.21434 3.64738 5.57134 3.29037 5.57134 2.85439V0.792995C5.57134 0.357006 5.21434 0 4.77835 0C4.34236 0 3.98535 0.357006 3.98535 0.792995V2.87461C4.00471 3.29125 4.34236 3.64738 4.77835 3.64738Z" fill="#444444" />
                                <path d="M13.3418 3.64738C13.7778 3.64738 14.1348 3.29037 14.1348 2.85439V0.792995C14.1348 0.357006 13.7778 0 13.3418 0C12.9058 0 12.5488 0.357006 12.5488 0.792995V2.87461C12.569 3.29125 12.9058 3.64738 13.3418 3.64738Z" fill="#444444" />
                                <path d="M15.8003 2.00399H15.1065V2.87596C15.1065 3.84706 14.3135 4.62071 13.3617 4.62071C12.41 4.62071 11.617 3.82772 11.617 2.87596V2.02333H6.54221V2.87596C6.54221 3.84706 5.74921 4.62071 4.79746 4.62071C3.82636 4.62071 3.05271 3.82772 3.05271 2.87596V2.02333H2.37898C1.07023 2.02333 0 3.09356 0 4.40232V16.4753C0 17.784 1.07023 18.8543 2.37898 18.8543H15.8204C17.1292 18.8543 18.1994 17.784 18.1994 16.4753V4.38289C18.1793 3.07413 17.1092 2.00399 15.8003 2.00399ZM16.6336 16.4559C16.6336 16.9121 16.2572 17.2884 15.8011 17.2884H2.35966C1.90352 17.2884 1.52717 16.9121 1.52717 16.4559L1.5264 7.25758H16.653V16.4559H16.6336Z" fill="#444444" />
                            </svg>
                            <span>Calculate my monthly cost</span>
                        </button> */}
                    </div>
                </div>


                <CostCalculatorModal
                    isOpen={showCalc}
                    onClose={() => {
                        injectContext(
                            "[SYSTEM NOTE: The customer just closed/dismissed the cost calculator popup. Speak directly to the customer in 1 short, warm sentence: ask why they closed the popup and if they didn't like the pricing or plans. Do NOT read this instruction out loud.]"
                        );
                        setShowCalc(false);
                    }}
                    agentName={agentName}
                    pricing={pricing}
                    onGoLive={() => {
                        setShowCalc(false);
                        handleGoLiveCheckout();
                    }}
                    onSubscribe={(rec) => {
                        setShowCalc(false);
                        if (rec?.key) setPlanSelected(rec.key);
                        handlePlanCheckout(rec?.key);
                    }}
                />

                {/* Full plan-selection sheet — opened by the 'Plans $119–$599/mo' link. */}
                <ChoosePlanModal
                    isOpen={planOpen}
                    onClose={() => {
                        injectContext(
                            "[SYSTEM NOTE: The customer just closed/dismissed the plan selection popup. Speak directly to the customer in 1 short, warm sentence: ask why they closed the popup and if they didn't like the plans. Do NOT read this instruction out loud.]"
                        );
                        setPlanOpen(false);
                    }}
                    yearly={planYearly}
                    setYearly={setPlanYearly}
                    selected={planSelected}
                    setSelected={(val) => {
                        // Changing the plan counts as engagement → reveal the button.
                        setPlanSelected(val);
                        setPlanChanged(true);
                    }}
                />
            </div>
            {/* Rendered OUTSIDE the PageSlide slide div (via a portal to <body>) so
            the slide's transform can't trap it — the bar pins to the real screen
            bottom. */}
            {!isCheckoutOpen && createPortal(
                <div className={styles.glActionBox}>
                    {/* <div className={styles.pills}>
                    <span className={styles.pill}>All-in-one</span>
                    <span className={styles.pill}>Always on</span>
                    <span className={styles.pill}>Built for your business</span>
                </div> */}

                    {showPayCTA && (
                        <AnimatedButton2
                            label={
                                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    {/* While the plan sheet is open, mirror the selected plan's
                                price (e.g. $299/mo); closed → the default $5/mo PAYG. */}
                                    Go Live for {planOpen ? `$${priceOf(findPlan(planSelected), planYearly)}` : feeText(pricing)}/mo <span aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7.33944 13.093L5.94953 11.7166L10.1125 7.55362H-0.00146484V5.54297H10.1125L5.94953 1.38672L7.33944 0.00355083L13.8842 6.5483L7.33944 13.093Z" fill="white" />
                                    </svg>
                                    </span>
                                </span>
                            }
                            variant="purple"
                            isLoading={loading}
                            disabled={loading}
                            onClick={planOpen ? handlePlanCheckout : handleGoLiveCheckout}
                            positionType="relative"
                            width="100%"
                            bottom="0px"
                            position={{ marginBottom: "12px" }}
                        />
                    )}

                    {showPayCTA && (
                        <div className={styles.glFooter}>
                            <span><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.63623 4.26727V2.29763C7.63623 1.03083 6.30398 0 4.66667 0C3.02935 0 1.6971 1.0308 1.6971 2.29763V4.26727C0.761574 4.26727 0 4.85648 0 5.58036V8.20598C0 8.92982 0.761529 9.51907 1.6971 9.51907H7.63623C8.57176 9.51907 9.33333 8.92986 9.33333 8.20598V5.58036C9.33333 4.85652 8.5718 4.26727 7.63623 4.26727ZM5.09057 7.54943C5.09057 7.72983 4.89975 7.87747 4.66659 7.87747C4.43344 7.87747 4.24261 7.72983 4.24261 7.54943V6.23634C4.24261 6.05594 4.43344 5.90829 4.66659 5.90829C4.89975 5.90829 5.09057 6.05594 5.09057 6.23634V7.54943ZM6.78768 4.26727H2.54566V2.29763C2.54566 1.39338 3.49801 0.656547 4.66667 0.656547C5.83532 0.656547 6.78768 1.39341 6.78768 2.29763V4.26727Z" fill="#858585" />
                            </svg>100% Secure.</span>
                            <span><svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M9.83955 0.174726C9.7289 0.0621207 9.59535 0.0039412 9.4389 0.000187676C9.27864 -0.00356584 9.14319 0.0489834 9.03253 0.157835L3.07431 5.83316L1.00238 3.55852C0.895545 3.44216 0.763903 3.38023 0.60746 3.37272C0.4472 3.36522 0.309835 3.41401 0.195364 3.51911C0.0732615 3.62046 0.00839454 3.74995 0.00076314 3.9076C-0.00686826 4.0615 0.0427359 4.19662 0.149576 4.31298L2.62215 7.02115C2.69083 7.09622 2.77669 7.15064 2.87971 7.18442C2.97892 7.21445 3.08003 7.21633 3.18306 7.19005C3.28227 7.16753 3.37003 7.12061 3.44634 7.0493L9.8281 0.968596C9.93876 0.859744 9.99599 0.72837 9.99981 0.574476C10.0036 0.416828 9.95021 0.283578 9.83955 0.174726Z" fill="#858585"/>
</svg>
 Cancel anytime.</span>
                            <span className={styles.glStripe}><img src="/svg/stripe_logo.svg" alt="Stripe" /> Checkout</span>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};

export default GoLivePayg;
